import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import moment from "moment";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import Formsy from "formsy-react";
import { Input, Textarea } from "formsy-react-components";
import { Row, Col } from "react-bootstrap";

import DropdownSearch from "../common/DropdownSearch";
import CurrencyInput from "../common/CurrencyInput";
import DateTimePicker from "../common/DateTimePicker";

import ContractService from "../../services/contractService";

import { formatDate } from "../../helpers/date";

const useContractNumber = (customer, adding) => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(undefined);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await ContractService.generateContractNumber();
        setCount(data);
        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
      }
    };
    if (customer._id) {
      setCount(customer.number);
    } else if (adding) {
      fetchData();
    }
  }, [customer._id, customer.number, adding]);
  return { loading, count, error };
};
const ContractForm = ({
  contract,
  customer,
  customers,
  onAdd,
  onSave,
  onCancel,
  users,
  codes,
  adding,
  editing,
  saving,
  preventEdit,
  onAddContact,
  contractTiers,
  savingContract,
  disableCustomerSelect
}) => {
  const [canSubmit, setCanSubmit] = useState(false);
  const [state, setState] = useState({});
  const { contract_type } = state;
  const { count } = useContractNumber(contract, adding);
  useEffect(() => {
    const loadDefaults = () => {
      if (adding) {
        const status =
          find(codes, { entity: "Contract", value: "Active" }) || {};
        setState((prevState) => ({ ...prevState, contract_status: status.id }));
      } else {
        setState((prevState) => ({
          ...prevState,
          contract_type: contract.contract_type
        }));
      }
      if (customer) setState((prevState) => ({ ...prevState, customer }));
    };
    loadDefaults();
  }, [codes, adding, customer, contract.contract_type]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      number: count
    }));
  }, [count]);
  const add = (model) => onAdd(model);

  const submit = (model) => {
    const data = { ...model, id: contract._id };
    if (data.start_date) {
      data.start_date = moment(data.start_date).toDate();
    }
    if (data.end_date) {
      data.end_date = moment(data.end_date).toDate();
    }
    if (data.entered_date) {
      data.entered_date = moment(data.entered_date).toDate();
    }
    if (data.support_hours_start) {
      data.support_hours_start = formatDate(
        data.support_hours_start,
        "hh:mm a"
      );
    }
    if (data.support_hours_end) {
      data.support_hours_end = formatDate(data.support_hours_end, "hh:mm a");
    }
    onSave(data);
  };

  const onChange = (field, selected) => {
    setState((prevState) => ({ ...prevState, [field]: selected.value }));
    if (field === "contract_type") {
      const ct = find(codes, { _id: selected.value });
      const customerName = state.customerName || contract.customer?.name || "";
      const contractName = `${customerName} - ${selected.label}`;
      setState((prevState) => ({
        ...prevState,
        contract_type: ct,
        contractName
      }));
    }
    if (field === "customer") {
      setState((prevState) => ({
        ...prevState,
        customerName: selected.label,
        contractName: selected.label,
        contract_type: ""
      }));
    }
  };

  const generateCodeOptions = (field) => {
    if (field === "type") {
      const codeTypeOptions = codes
        .filter(
          (code) =>
            code.entity === "Contract" &&
            code.field === field &&
            code.label !== "CAPITAL" &&
            code.label !== "TIER1" &&
            code.label !== "TIER2" &&
            code.label !== "CLIENTCARE" &&
            code.label !== "AS BILLED"
        )
        .map((code) => ({
          value: code._id,
          label: code.label
        }));

      return orderBy(codeTypeOptions, "label");
    }
    const codeOptions = codes
      .filter((code) => code.entity === "Contract" && code.field === field)
      .map((code) => ({
        value: code._id,
        label: code.label
      }));

    return orderBy(codeOptions, "label");
  };

  const generateContractTierOptions = () => {
    const tierOptions =
      contractTiers && contractTiers.length && contract_type
        ? contractTiers
            .filter(
              (tier) =>
                tier.status && contract_type._id === tier.contract_type._id
            )
            .map((tier) => ({
              value: tier._id,
              label: tier.label
            }))
        : [];
    return tierOptions;
  };

  const renderBillingInfo = () => {
    if (!isEmpty(contract_type)) {
      switch (contract_type.label) {
        case "BILLABLE":
          return (
            <BillingRate
              contract={contract}
              editing={editing}
              onChange={onChange}
              codes={generateCodeOptions}
              {...state}
            />
          );
        case "RETAINER":
        case "CLIENTCARE":
        case "MANAGED SERVICES":
          return (
            <ClientCare
              contract={contract}
              editing={editing}
              onChange={onChange}
              codes={generateCodeOptions}
              {...state}
            />
          );
        case "FIXED FEE":
          return (
            <FixedFee
              contract={contract}
              editing={editing}
              onChange={onChange}
              codes={generateCodeOptions}
              {...state}
            />
          );
        case "PREPAID":
          return (
            <PrePaid
              contract={contract}
              editing={editing}
              onChange={onChange}
              codes={generateCodeOptions}
              {...state}
            />
          );
        default:
          return null;
      }
    }
  };

  return (
    <Formsy
      className="form"
      onValidSubmit={adding ? add : submit}
      onValid={() => setCanSubmit(true)}
      onInvalid={() => setCanSubmit(false)}
    >
      <Row>
        <Col md={12} xs={12}>
          <fieldset>
            <legend>Contract Info</legend>
          </fieldset>
        </Col>
        <Col md={6} xs={12}>
          <Input
            required={!!editing}
            type="text"
            name="number"
            label="Contract Number"
            disabled
            value={state.number}
            readOnly
          />
        </Col>
        <Col md={6} xs={12}>
          <DropdownSearch
            required
            name="customer"
            label="Customer"
            disabled={!editing || customer || disableCustomerSelect}
            onChange={onChange}
            value={
              customer?._id ||
              state.customer ||
              (contract.customer && contract.customer._id)
            }
            data={orderBy(
              [
                ...customers.map((c) => ({
                  value: c._id,
                  label: c.name
                }))
              ],
              "label"
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6} xs={12}>
          <DropdownSearch
            required
            name="contract_type"
            label="Contract Type"
            disabled={!editing}
            onChange={onChange}
            value={contract_type ? contract_type.id : ""}
            data={[...generateCodeOptions("type")]}
          />
        </Col>
        <Col md={6} xs={12}>
          <Input
            required={!!editing}
            name="name"
            label="Contract Name"
            value={state.contractName || contract.name}
            disabled={!editing}
            validations={{ maxLength: 150 }}
            validationErrors={{
              maxLength: "You can not type in more than 150 characters"
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col md={4} xs={12}>
          <DropdownSearch
            required
            name="contract_status"
            label="Status"
            disabled={!editing}
            onChange={onChange}
            value={
              state.contract_status ||
              (contract?.contract_status?._id
                ? contract.contract_status._id
                : contract.contract_status)
            }
            data={orderBy(
              [
                ...codes
                  .filter(
                    (code) =>
                      code.entity === "Contract" && code.field === "status"
                  )
                  .map((code) => ({
                    value: code._id,
                    label: code.label,
                    order: code.order
                  }))
              ],
              "order"
            )}
          />
        </Col>
        <Col md={4} xs={12}>
          <Input
            required={!!editing}
            type="date"
            name="entered_date"
            label="Entered Date"
            disabled={!editing}
            value={formatDate(contract.entered_date, "yyyy-MM-dd")}
          />
        </Col>
        <Col md={4} xs={12}>
          <DropdownSearch
            name="salesperson"
            label="Sales Person"
            value={
              state.salesperson ||
              (contract?.salesperson?._id
                ? contract.salesperson._id
                : contract.salesperson)
            }
            disabled={!editing}
            data={orderBy(
              [
                ...users.map((user) => ({
                  value: user._id,
                  label: `${user.last_name}, ${user.first_name}`
                }))
              ],
              "label"
            )}
            onChange={(name, selected) =>
              selected.value === null
                ? onAddContact()
                : onChange(name, selected)
            }
          />
        </Col>
      </Row>
      {!!generateContractTierOptions().length && (
        <Row>
          <Col md={4} xs={12}>
            <DropdownSearch
              name="tier"
              label="Tier"
              disabled={!editing}
              onChange={onChange}
              value={
                state.tier ||
                (contract?.tier?._id ? contract.tier._id : contract.tier)
              }
              data={orderBy(generateContractTierOptions(), "label")}
            />
          </Col>
          <Col md={4} xs={12}>
            <DateTimePicker
              showTimeSelectOnly
              name="support_hours_start"
              label="Support Hours Start"
              onChange={(date, name) => onChange(name, { value: date })}
              disabled={!editing}
              value={
                // eslint-disable-next-line no-nested-ternary
                state.support_hours_start
                  ? state.support_hours_start
                  : contract.support_hours_start
                    ? moment(contract.support_hours_start, "hh:mm A").toDate()
                    : null
              }
              minTime={moment().startOf("day")}
              maxTime={
                // eslint-disable-next-line no-nested-ternary
                state.support_hours_end
                  ? state.support_hours_end
                  : contract.support_hours_end
                    ? moment(contract.support_hours_end, "hh:mm A")
                    : moment().endOf("day")
              }
            />
          </Col>
          <Col md={4} xs={12}>
            <DateTimePicker
              showTimeSelectOnly
              name="support_hours_end"
              label="Support Hours End"
              onChange={(date, name) => onChange(name, { value: date })}
              disabled={!editing}
              value={
                // eslint-disable-next-line no-nested-ternary
                state.support_hours_end
                  ? state.support_hours_end
                  : contract.support_hours_end
                    ? moment(contract.support_hours_end, "hh:mm A").toDate()
                    : null
              }
              minTime={
                // eslint-disable-next-line no-nested-ternary
                state.support_hours_start
                  ? state.support_hours_start
                  : contract.support_hours_start
                    ? moment(contract.support_hours_start, "hh:mm A")
                    : moment().endOf("day")
              }
              maxTime={moment().endOf("day")}
            />
          </Col>
        </Row>
      )}

      <Row>
        <Col xs={12}>
          <Textarea
            name="details"
            label="Description"
            value={contract.details}
            disabled={!editing}
            validations={{ maxLength: 3000 }}
            validationErrors={{
              maxLength: "You can not type in more than 3000 characters"
            }}
          />
        </Col>
      </Row>

      <Row>
        <Col md={3} xs={12}>
          <Input
            required={
              contract_type?.label === "RETAINER" ||
              contract_type?.label === "CLIENTCARE"
            }
            type="date"
            name="start_date"
            label="Start Date"
            disabled={!editing}
            value={formatDate(contract.start_date, "yyyy-MM-dd")}
          />
        </Col>
        <Col md={3} xs={12}>
          <Input
            required={
              adding &&
              (contract_type?.label === "RETAINER" ||
                contract_type?.label === "CLIENTCARE")
            }
            type="date"
            name="end_date"
            label="End Date"
            disabled={!editing}
            value={
              contract.end_date
                ? formatDate(contract.end_date, "yyyy-MM-dd")
                : undefined
            }
            validations={{
              isValidDate: (values, value) =>
                !value || moment(value).isSameOrAfter(values.start_date)
            }}
            validationErrors={{
              isValidDate: "End date should be after start date"
            }}
          />
        </Col>
        {renderBillingInfo()}
        <Col
          md={
            contract_type?.label === "BILLABLE" ||
            contract_type?.label === "FIXED FEE"
              ? 8
              : 12
          }
          xs={12}
        >
          <Textarea
            name="service_note"
            label="Service Note"
            value={contract.service_note}
            disabled={!editing}
            validations={{ maxLength: 3000 }}
            validationErrors={{
              maxLength: "You can not type in more than 3000 characters"
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12} xs={12} className="footer">
          <button className="btn btn-cancel" type="button" onClick={onCancel}>
            Cancel
          </button>
          <input
            className="btn btn-success"
            type="submit"
            disabled={!canSubmit || preventEdit || saving || savingContract}
            value={saving ? "Saving... " : "Save" || ""}
          />
        </Col>
      </Row>
    </Formsy>
  );
};

const InvoiceFrequency = ({
  contract,
  editing,
  invoice_frequency,
  invoice_format,
  invoice_format_codes,
  invoice_frequency_codes,
  onChange
}) => (
  <React.Fragment>
    <Col md={12} xs={12}>
      <fieldset>
        <legend>Invoice</legend>
      </fieldset>
    </Col>
    <Col md={3} xs={12}>
      <DropdownSearch
        name="invoice_frequency"
        label="Frequency"
        disabled={!editing}
        onChange={onChange}
        value={invoice_frequency || contract.invoice_frequency}
        data={invoice_frequency_codes}
      />
    </Col>
    <Col md={3} xs={12}>
      <DropdownSearch
        name="invoice_format"
        label="Distribution Format"
        disabled={!editing}
        onChange={onChange}
        value={invoice_format || contract.invoice_format}
        data={invoice_format_codes}
      />
    </Col>
    <Col md={3} xs={12}>
      <Input
        name="day_to_send_invoice"
        type="number"
        label="Day To Send"
        disabled={!editing}
        value={contract.day_to_send_invoice}
      />
    </Col>
    <Col md={3} xs={12}>
      <Input
        name="invoice_email"
        label="Email Address"
        disabled={!editing}
        value={contract.invoice_email}
        validations={{ maxLength: 250 }}
        validationErrors={{
          maxLength: "You can not type in more than 250 characters"
        }}
      />
    </Col>
  </React.Fragment>
);

InvoiceFrequency.propTypes = {
  contract: PropTypes.object,
  editing: PropTypes.bool,
  invoice_frequency_codes: PropTypes.array,
  invoice_format_codes: PropTypes.array,
  invoice_frequency: PropTypes.any,
  invoice_format: PropTypes.any,
  onChange: PropTypes.func
};

const FixedFee = ({ contract, editing, ...rest }) => (
  <React.Fragment>
    <Col md={3} xs={12}>
      <CurrencyInput
        type="number"
        name="billing_rate"
        label="Billing Rate"
        disabled={!editing}
        value={contract.billing_rate || 0}
      />
    </Col>
    <Col md={3} xs={12}>
      <DropdownSearch
        name="invoice_frequency"
        label="Invoice Frequency"
        disabled={!editing}
        onChange={rest.onChange}
        value={rest.invoice_frequency || contract.invoice_frequency}
        data={[...rest.codes("frequency")]}
      />
    </Col>
  </React.Fragment>
);
FixedFee.propTypes = {
  contract: PropTypes.object,
  editing: PropTypes.bool
};

const BillingRate = ({ contract, editing, ...rest }) => (
  <React.Fragment>
    <Col md={3} xs={12}>
      <CurrencyInput
        type="number"
        name="billing_rate"
        label="Billing Rate"
        disabled={!editing}
        value={contract.billing_rate || 0}
      />
    </Col>
    <Col md={3} xs={12}>
      <DropdownSearch
        name="invoice_frequency"
        label="Invoice Frequency"
        disabled={!editing}
        onChange={rest.onChange}
        value={rest.invoice_frequency || contract.invoice_frequency}
        data={[...rest.codes("frequency")]}
      />
    </Col>
  </React.Fragment>
);
BillingRate.propTypes = {
  contract: PropTypes.object,
  editing: PropTypes.bool
};

const ClientCare = ({ contract, editing, ...rest }) => (
  <React.Fragment>
    <Col md={3} xs={12}>
      <CurrencyInput
        type="number"
        name="annual_amt"
        label="Annual Amount"
        disabled={!editing}
        value={contract.annual_amt}
      />
    </Col>
    <Col md={3} xs={12}>
      <CurrencyInput
        type="number"
        name="monthly_amt"
        label="Monthly Amount"
        disabled={!editing}
        value={contract.monthly_amt}
      />
    </Col>
    <Col md={3} xs={12}>
      <DropdownSearch
        name="invoice_frequency"
        label="Invoice Frequency"
        disabled={!editing}
        onChange={rest.onChange}
        value={rest.invoice_frequency || contract.invoice_frequency}
        data={[...rest.codes("frequency")]}
      />
    </Col>
  </React.Fragment>
);

ClientCare.propTypes = {
  contract: PropTypes.object,
  editing: PropTypes.bool
};

const PrePaid = ({ contract, editing, ...rest }) => (
  <React.Fragment>
    <Col md={4} xs={12}>
      <CurrencyInput
        type="number"
        name="billing_rate"
        label="Billing Rate"
        disabled={!editing}
        value={contract.billing_rate || 0}
      />
    </Col>
    <Col md={3} xs={12}>
      <DropdownSearch
        name="invoice_frequency"
        label="Invoice Frequency"
        disabled={!editing}
        onChange={rest.onChange}
        value={rest.invoice_frequency || contract.invoice_frequency}
        data={[...rest.codes("frequency")]}
      />
    </Col>
    <Col md={3} xs={12}>
      <DropdownSearch
        name="invoice_format"
        label="Distribution Format"
        disabled={!editing}
        onChange={rest.onChange}
        value={rest.invoice_format || contract.invoice_format}
        data={[...rest.codes("invoice_format")]}
      />
    </Col>
    <Col md={3} xs={12}>
      <Input
        name="day_to_send_invoice"
        label="Day To Send"
        disabled={!editing}
        value={contract.day_to_send_invoice}
      />
    </Col>
    <Col md={3} xs={12}>
      <Input
        name="invoice_email"
        label="Email Address"
        value={contract.invoice_email}
        validations={{ maxLength: 250 }}
        validationErrors={{
          maxLength: "You can not type in more than 250 characters"
        }}
      />
    </Col>
    <Col md={12} xs={12}>
      <fieldset>
        <legend>Report</legend>
      </fieldset>
    </Col>
    <Col md={3} xs={12}>
      <DropdownSearch
        name="report"
        label="Report"
        disabled={!editing}
        onChange={rest.onChange}
        value={rest.report || contract.report}
        data={[...rest.codes("report_format")]}
      />
    </Col>
    <Col md={3} xs={12}>
      <DropdownSearch
        name="report_frequency"
        label="Report Frequency"
        disabled={!editing}
        onChange={rest.onChange}
        value={rest.report_frequency || contract.report_frequency}
        data={[...rest.codes("frequency")]}
      />
    </Col>
    <Col md={3} xs={12}>
      <DropdownSearch
        name="report_format"
        label="Distribution Format"
        disabled={!editing}
        onChange={rest.onChange}
        value={rest.report_format || contract.report_format}
        data={[...rest.codes("invoice_format")]}
      />
    </Col>
    <Col md={3} xs={12}>
      <Input
        name="day_to_send_report"
        type="number"
        label="Day To Send"
        disabled={!editing}
        value={contract.day_to_send_report}
      />
    </Col>
    <Col md={12} xs={12}>
      <Input
        name="report_email"
        label="Email Address"
        disabled={!editing}
        value={contract.report_email}
        validations={{ maxLength: 250 }}
        validationErrors={{
          maxLength: "You can not type in more than 250 characters"
        }}
      />
    </Col>
  </React.Fragment>
);

PrePaid.propTypes = {
  contract: PropTypes.object,
  editing: PropTypes.bool
};

ContractForm.propTypes = {
  contract: PropTypes.object,
  customer: PropTypes.object,
  customers: PropTypes.array,
  onAdd: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  users: PropTypes.array,
  codes: PropTypes.array,
  adding: PropTypes.bool,
  editing: PropTypes.bool,
  saving: PropTypes.bool,
  preventEdit: PropTypes.bool,
  onAddContact: PropTypes.func,
  contractTiers: PropTypes.array,
  savingContract: PropTypes.bool,
  disableCustomerSelect: PropTypes.bool
};

ContractForm.defaultProps = {
  disableCustomerSelect: false
};

export default ContractForm;
